import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { GoogleLogo, EnvelopeSimple } from "@phosphor-icons/react";
import api from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success(`Selamat datang kembali, ${data?.workspace?.name || data?.user?.full_name || ""}!`);
      navigate("/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal masuk");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = async () => {
    // Simplified demo Google login - in production this would use Google Identity Services
    setLoading(true);
    try {
      const fakeGoogle = {
        email: "google.demo@example.com",
        full_name: "Google Demo",
        google_sub: "demo-" + Math.random().toString(36).slice(2, 10),
        workspace_name: "Google Demo Workspace",
      };
      const r = await api.post("/auth/google", fakeGoogle);
      localStorage.setItem("nusa_token", r.data.access_token);
      toast.success("Login Google berhasil (demo)");
      window.location.href = "/app/dashboard";
    } catch (err) {
      toast.error("Login Google gagal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FDFDFC]">
      <div className="hidden lg:block relative overflow-hidden bg-slate-900">
        <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(ellipse at 30% 40%, rgba(217,119,6,0.5), transparent 50%)" }} />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Link to="/" className="font-serif-display text-3xl">AIAkuntan</Link>
          <div>
            <h2 className="font-serif-display text-4xl leading-tight">"Sekarang tim akuntan saya bisa fokus ke strategi, bukan input data."</h2>
            <div className="mt-6 text-sm text-slate-400">— Adinda P., Founder Kopi Nusantara</div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md" data-testid="login-page">
          <div className="lg:hidden mb-8">
            <Link to="/" className="font-serif-display text-3xl text-slate-900">AIAkuntan</Link>
          </div>
          <h1 className="font-serif-display text-4xl font-medium text-slate-900">Masuk ke AIAkuntan</h1>
          <p className="mt-2 text-slate-600 text-sm">Kelola bisnis nusantara Anda dengan cerdas.</p>

          <Button
            onClick={googleLogin}
            variant="outline"
            className="w-full mt-8 h-11 rounded-md border-slate-300"
            data-testid="google-login-btn"
            disabled={loading}
          >
            <GoogleLogo size={18} weight="bold" className="mr-2" />
            Masuk dengan Google
          </Button>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-500 uppercase tracking-wider">atau</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 h-11"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1 h-11"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white rounded-md"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? "Memproses..." : "Masuk"}
            </Button>
          </form>
          <div className="text-sm text-center mt-6 text-slate-600">
            Belum punya akun? <Link to="/register" className="text-amber-700 font-medium hover:underline" data-testid="signup-link">Daftar gratis</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
